import Papa from 'papaparse';
import readExcelFile from 'read-excel-file/browser';

const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/

const cellToString = (value) => value === undefined || value === null ? '' : value.toString().trim()
const isMissing = (value) => cellToString(value) === ''
const normalizeHeader = (value) => cellToString(value).replaceAll(/^\uFEFF/g, '').toLowerCase().replaceAll(/[^a-z0-9]/g, '')
const toOptionalString = (value) => isMissing(value) ? null : cellToString(value)
const wholeNumberRegex = /^\d+$/
const normalizePostcode = (value) => {
   const text = cellToString(value)

   if (!text) {
       return null
   }

   return wholeNumberRegex.test(text) && text.length === 3 ? text.padStart(4, '0') : text
}

const integerValidator = ({ value, label, required = true, min = 1 }) => {
   if (isMissing(value)) {
       return required ? `${label} is required` : null
   }

   const text = cellToString(value)

   if (!wholeNumberRegex.test(text)) {
       return `${label} must be a whole number`
   }


   if (Number.parseInt(text, 10) < min) {
       return min === 1 ? `${label} must be greater than 0` : `${label} must be ${min} or greater`
   }

   return null
}

const columns = [
   {
       key: 'PracticeName',
       label: 'PracticeName',
       required: true,
       aliases: ['Practice Name'],
       normalize: cellToString,
       validate: value => {
           const text = cellToString(value)

           if (!text) return 'Practice Name is required'
           if (text.includes(',') || text.includes('"')) return 'Practice Name cannot contain special characters like , or "'
           if (text.length > 255) return 'Practice Name must not exceed 255 characters'

           return null
       }
   },
   {
       key: 'Abbreviation',
       label: 'Abbreviation',
       required: true,
       aliases: ['Practice Abbreviation'],
       normalize: cellToString,
       validate: value => {
           const text = cellToString(value)

           if (!text) return 'Practice Abbreviation is required'
           if (text.length > 20) return 'Practice Abbreviation must not exceed 20 characters'

           return null
       }
   },
   {
       key: 'PipQIStatusID',
       label: 'PipQIStatusID',
       required: true,
       aliases: ['PIPQIStatusID', 'PIP QI Status ID', 'PIP QI Status'],
       normalize: toOptionalString,
       validate: value => integerValidator({ value, label: 'PipQIStatusID' })
   },
   {
       key: 'Postcode',
       label: 'Postcode',
       required: true,
       aliases: ['PostCode', 'Post Code'],
       normalize: normalizePostcode,
       validate: value => {
           const text = cellToString(value)

           if (!text) return 'Postcode is required'
           if (!wholeNumberRegex.test(text) || !/^\d{3,4}$/.test(text)) return 'Postcode must be a 3 or 4 digit number'
           if (Number.parseInt(text, 10) <= 0 || Number.parseInt(text, 10) > 9999) return 'Postcode must be between 1 and 9999'

           return null
       }
   },
   {
       key: 'PracticeGroupId',
       label: 'PracticeGroupId',
       required: false,
       aliases: ['PracticeGroupID', 'Practice Group ID', 'Practice Group'],
       normalize: value => isMissing(value) ? '0' : cellToString(value),
       validate: value => integerValidator({ value, label: 'PracticeGroupId', required: false, min: 0 })
   },
   {
       key: 'PracticeSoftwareID',
       label: 'PracticeSoftwareID',
       required: true,
       aliases: ['PracticeSoftwareId', 'Practice Software ID', 'Practice Software', 'Software ID', 'Software'],
       normalize: toOptionalString,
       validate: value => integerValidator({ value, label: 'Practice SoftwareID' })
   },
   {
       key: 'ManagerEmail',
       label: 'ManagerEmail',
       required: false,
       aliases: ['Manager Email'],
       normalize: value => isMissing(value) ? undefined : cellToString(value),
       validate: value => !isMissing(value) && !emailRegex.test(cellToString(value)) ? 'Invalid Manager Email' : null
   },
   {
       key: 'ITProviderEmail',
       label: 'ITProviderEmail',
       required: false,
       aliases: ['IT Provider Email', 'ITProvider Email'],
       normalize: value => isMissing(value) ? undefined : cellToString(value),
       validate: value => !isMissing(value) && !emailRegex.test(cellToString(value)) ? 'Invalid IT Provider Email' : null
   }
]

const columnsByHeader = columns.reduce((result, column) => {
   [column.label, column.key, ...column.aliases].forEach(alias => {
       result.set(normalizeHeader(alias), column)
   })

   return result
}, new Map())

export const BULK_UPLOAD_HEADERS = columns.map(column => column.label)
export const BULK_UPLOAD_TEMPLATE_CSV = `${BULK_UPLOAD_HEADERS.join(',')}\n`
export const MAX_VISIBLE_BULK_UPLOAD_ERRORS = 25

const isEmptyRow = row => !row || row.every(cell => isMissing(cell))

const getHeaderMetadata = (headerRow = []) => {
   const columnIndexes = {}
   const errors = []

   headerRow.forEach((header, index) => {
       const headerText = cellToString(header)

       if (!headerText) {
           return
       }

       const column = columnsByHeader.get(normalizeHeader(headerText))

       if (!column) {
           errors.push(`Unknown column "${headerText}"`)
           return
       }

       if (columnIndexes[column.key] !== undefined) {
           errors.push(`Column "${column.label}" appears more than once`)
           return
       }

       columnIndexes[column.key] = index
   })

   columns
       .filter(column => column.required && columnIndexes[column.key] === undefined)
       .forEach(column => errors.push(`Missing required column "${column.label}"`))

   return { columnIndexes, errors }
}

const buildPractice = (row, metadata) => {
   const practice = {}

   columns.forEach(column => {
       const index = metadata.columnIndexes[column.key]

       if (index !== undefined) {
           const value = column.normalize(row[index])


           if (value !== undefined) {
               practice[column.key] = value
           }
       }
       else if (column.key === 'PracticeGroupId') {
           practice[column.key] = '0'
       }
   })

   return practice
}

const validatePractice = (practice, rowNumber) => columns.flatMap(column => {
   const error = column.validate(practice[column.key])

   return error ? [`Row ${rowNumber}: ${error}`] : []
})

const addDuplicateErrors = (practices, errors) => {
   [
       { key: 'PracticeName', label: 'PracticeName' },
       { key: 'Abbreviation', label: 'Abbreviation' }
   ].forEach(({ key, label }) => {
       const seen = new Map()

       practices.forEach(({ practice, rowNumber }) => {
           const value = cellToString(practice[key]).toLowerCase()

           if (!value) {
               return
           }

           if (seen.has(value)) {
               errors.push(`Rows ${seen.get(value)} and ${rowNumber}: ${label} "${practice[key]}" is duplicated in this file`)
               return
           }


           seen.set(value, rowNumber)
       })
   })
}

const getHeaderRowIndex = (rows = []) => (rows ?? []).findIndex(row => !isEmptyRow(row))

export const parseBulkUploadRows = (rows = []) => {
   const uploadRows = rows ?? []
   const headerRowIndex = getHeaderRowIndex(uploadRows)


   if (headerRowIndex === -1) {
       return { data: [], errors: ['The upload file is empty'] }
   }

   const headerRow = uploadRows[headerRowIndex] ?? []
   const metadata = getHeaderMetadata(headerRow)


   if (metadata.errors.length > 0) {
       return { data: [], errors: metadata.errors }
   }

   const parsedRows = []
   const errors = []

   uploadRows.slice(headerRowIndex + 1).forEach((row, index) => {
       if (isEmptyRow(row)) {
           return
       }

       const rowNumber = headerRowIndex + index + 2
       const practice = buildPractice(row, metadata)
       const rowErrors = validatePractice(practice, rowNumber)


       if (rowErrors.length > 0) {
           errors.push(...rowErrors)
           return
       }


       parsedRows.push({ practice, rowNumber })
   })

   if (parsedRows.length === 0 && errors.length === 0) {
       errors.push('No practice rows found')
   }


   addDuplicateErrors(parsedRows, errors)

   return {
       data: errors.length > 0 ? [] : parsedRows.map(row => row.practice),
       errors
   }
}

export const mapRowsToBulkUploadData = (rows = []) => parseBulkUploadRows(rows).data

export const hasBulkUploadHeaders = (rows = []) => {
   const uploadRows = rows ?? []
   const headerRowIndex = getHeaderRowIndex(uploadRows)


   if (headerRowIndex === -1) {
       return false
   }

   const metadata = getHeaderMetadata(uploadRows[headerRowIndex] ?? [])

   return columns
       .filter(column => column.required)
       .every(column => metadata.columnIndexes[column.key] !== undefined)
}

const getSheetData = sheet => Array.isArray(sheet) ? sheet : sheet?.data

export const selectBulkUploadSheetRows = (sheets = []) => {
   const matchingSheet = (sheets ?? []).find(sheet => hasBulkUploadHeaders(getSheetData(sheet)))


   if (!matchingSheet) {
       throw new Error('No worksheet contains the required bulk practice upload headers.')
   }

   return getSheetData(matchingSheet)
}

export const validateBulkUploadData = (bulkData = []) => {
   const parsedRows = (bulkData ?? []).map((practice, index) => ({
       practice: {
           PracticeGroupId: '0',
           ...practice
       },
       rowNumber: index + 2
   }))
   const errors = parsedRows.flatMap(row => validatePractice(row.practice, row.rowNumber))

   addDuplicateErrors(parsedRows, errors)

   return errors
}

export const parseCsvRows = (csvText) => {
   const result = Papa.parse(csvText)
   const errors = result.errors.filter(error => error.code !== 'UndetectableDelimiter')

   if (errors.length > 0) {
       throw new Error(errors.map(error => error.message).join(' '))
   }

   return result.data
}

export const readUploadRows = async (uploadFile) => {
   const fileName = uploadFile?.name.toLowerCase() ?? ''

   if (fileName.endsWith('.xlsx')) {
       return selectBulkUploadSheetRows(await readExcelFile(uploadFile))
   }


   if (fileName.endsWith('.csv')) {
       return parseCsvRows(await uploadFile.text())
   }

   throw new Error('Please note: Only excel or csv file formats are allowed.')
}

const flattenErrorValues = value => {
   if (Array.isArray(value)) {
       return value.flatMap(flattenErrorValues)
   }

   if (value && typeof value === 'object') {
       return Object.values(value).flatMap(flattenErrorValues)
   }

   return value ? [value.toString()] : []
}

export const getBulkUploadErrorMessage = (exception) => {
   const data = exception?.response?.data
   const messages = data?.errors ? flattenErrorValues(data.errors).filter(Boolean) : []


   if (messages.length > 0) {
       return messages.join(' ')
   }

   return data?.title ?? exception?.message ?? 'File upload failed'
}